import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Network, Search, Building2, MapPin, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function CircuitSearch() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [circuits, setCircuits] = useState([]);
  const [customers, setCustomers] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [loading, setLoading] = useState(false);

  const fetchCircuits = async () => {
    setLoading(true);
    try {
      let url = `${API}/circuits`;
      const params = new URLSearchParams();
      if (statusFilter === 'delivered') params.append('status', 'delivered');
      if (statusFilter === 'pending') params.append('status', 'pending');
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      let filteredCircuits = response.data;
      
      // Client-side search by ANT Circuit ID, Provider Circuit ID, VLAN, etc.
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filteredCircuits = filteredCircuits.filter(c => 
          c.ant_circuit_id?.toLowerCase().includes(term) ||
          c.provider_circuit_id?.toLowerCase().includes(term) ||
          c.location_name?.toLowerCase().includes(term) ||
          c.ra_number?.toLowerCase().includes(term) ||
          c.vlan_id?.toLowerCase().includes(term) ||
          c.provider?.toLowerCase().includes(term) ||
          c.city?.toLowerCase().includes(term) ||
          c.bandwidth?.toString().includes(term) ||
          c.end_a_port_details?.toLowerCase().includes(term) ||
          c.end_b_port_details?.toLowerCase().includes(term)
        );
      }
      
      setCircuits(filteredCircuits);
      
      // Fetch customer names for each circuit
      const customerIds = [...new Set(filteredCircuits.map(c => c.customer_id))];
      const customerMap = {};
      for (const customerId of customerIds) {
        try {
          const custRes = await axios.get(`${API}/customers/${customerId}`);
          customerMap[customerId] = custRes.data;
        } catch (e) {
          customerMap[customerId] = { company_name: 'Unknown' };
        }
      }
      setCustomers(customerMap);
    } catch (error) {
      toast.error('Failed to fetch circuits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCircuits();
  }, [API, statusFilter]);

  const handleSearch = () => {
    fetchCircuits();
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    setSearchParams(params);
  };

  return (
    <div className="space-y-6" data-testid="circuit-search-page">
      <div>
        <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">Circuit Search</h2>
        <p className="text-slate-600 mt-1">Search by ANT Circuit ID, Provider ID, VLAN, RA Number, Location, Provider or City</p>
      </div>

      {/* Search Card */}
      <Card>
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by ANT Circuit ID, VLAN, Provider, RA Number, Location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-11 h-12 text-lg"
                data-testid="circuit-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-12">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} className="h-12 px-6 bg-slate-900 hover:bg-slate-800">
              <Search className="mr-2 h-4 w-4" /> Search
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-sm text-slate-500">{circuits.length} circuit(s) found</p>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : circuits.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Network className="mx-auto h-16 w-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No circuits found</p>
            <p className="text-slate-400 text-sm mt-1">Try searching with a different Circuit ID or keyword</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {circuits.map((circuit) => {
            const customer = customers[circuit.customer_id];
            return (
              <Card key={circuit.circuit_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-sky-100 px-4 py-3 rounded-lg">
                        <p className="text-xs text-sky-600 font-medium">ANT Circuit ID</p>
                        <p className="font-mono font-bold text-sky-800 text-xl">{circuit.ant_circuit_id}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-lg">{circuit.location_name}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin className="h-4 w-4" />
                          {circuit.installation_address}, {circuit.city}, {circuit.state} - {circuit.pincode}
                        </div>
                      </div>
                    </div>
                    <Badge className={circuit.is_delivered ? 'bg-emerald-100 text-emerald-700 px-3 py-1' : 'bg-amber-100 text-amber-700 px-3 py-1'}>
                      {circuit.is_delivered ? <><CheckCircle className="h-4 w-4 mr-1" /> Delivered</> : <><Clock className="h-4 w-4 mr-1" /> Pending</>}
                    </Badge>
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                    <Building2 className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Customer</p>
                      <p className="font-medium text-slate-900">
                        <a href={`/customers/${circuit.customer_id}`} onClick={(e) => { e.preventDefault(); navigate(`/customers/${circuit.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                          {customer?.company_name || 'Loading...'}
                        </a>
                      </p>
                    </div>
                    <div className="ml-auto">
                      <p className="text-xs text-slate-500">{customer?.customer_code}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/customers/${circuit.customer_id}`)}
                    >
                      View Customer
                    </Button>
                  </div>

                  {/* Technical Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-slate-500 text-xs">Bandwidth</p>
                      <p className="font-semibold text-slate-900">{circuit.bandwidth} Mbps</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-slate-500 text-xs">Last Mile</p>
                      <p className="font-semibold text-slate-900">{circuit.last_mile_type}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-slate-500 text-xs">Monthly Charges</p>
                      <p className="font-semibold text-slate-900">₹{circuit.monthly_charges?.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-slate-500 text-xs">IP Allocation</p>
                      <p className="font-semibold text-slate-900">{circuit.ip_allocation || 'Dynamic'}</p>
                    </div>
                  </div>

                  {/* Delivery Details */}
                  {circuit.is_delivered && (
                    <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" /> Delivery Information
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-emerald-600 text-xs">Provider</p>
                          <p className="font-medium text-slate-800">{circuit.provider || '-'}</p>
                        </div>
                        <div>
                          <p className="text-emerald-600 text-xs">Provider Circuit ID</p>
                          <p className="font-mono text-slate-800">{circuit.provider_circuit_id || '-'}</p>
                        </div>
                        <div>
                          <p className="text-emerald-600 text-xs">RA Number</p>
                          <p className="font-mono text-slate-800">{circuit.ra_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-emerald-600 text-xs">VLAN ID</p>
                          <p className="font-mono text-slate-800">{circuit.vlan_id || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-emerald-600 text-xs">End A (ISP Side)</p>
                          <p className="text-slate-800">{circuit.end_a_port_details || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-emerald-600 text-xs">End B (Customer Side)</p>
                          <p className="text-slate-800">{circuit.end_b_port_details || '-'}</p>
                        </div>
                        <div>
                          <p className="text-emerald-600 text-xs">Delivery Date</p>
                          <p className="text-slate-800">{circuit.delivery_date || '-'}</p>
                        </div>
                        <div>
                          <p className="text-emerald-600 text-xs">Delivered By</p>
                          <p className="text-slate-800">{circuit.delivered_by || '-'}</p>
                        </div>
                        {circuit.delivery_notes && (
                          <div className="col-span-2">
                            <p className="text-emerald-600 text-xs">Notes</p>
                            <p className="text-slate-800">{circuit.delivery_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
